import { useT } from '../i18n/locale-context';
import { formatPhone } from '../lib/format';
import { GlobeIcon, PhoneIcon, PinIcon, TagIcon, TelegramIcon } from './icons';
import type { WinnerContact as WinnerContactData } from '../lib/catalog-client';
import { track } from '../lib/funnel';
import { COMPANY, HAS_TELEGRAM } from '../lib/company';
import { ContactSupport } from './contact-support';

/** Which contact link was used — `winner_contact_clicked{kind}`. */
export type WinnerContactClickKind = 'tel' | 'telegram' | 'website' | 'email';

/**
 * One stored phone value → the numbers inside it, each with a dialable `tel:`
 * href and the site's display form.
 *
 * ~95% of `organizations.phone` is clean E.164 (`+998901234567`), but the
 * older rows hold a bare 9-digit local number or two numbers joined by a comma
 * («901234567, 971234567»). Fed whole to `tel:` those dial nothing, so the value
 * is split first. The href is E.164 whenever the number is a recognisable UZ
 * mobile/landline (12 digits with 998, or a bare 9); anything else keeps its own
 * digits (and a leading `+` if it had one) rather than being guessed at.
 * Display reuses `formatPhone`, the formatter the заказчик contact on the same
 * page already uses, so one page never shows two groupings.
 */
export function phoneEntries(raw: string | null | undefined): { href: string; label: string }[] {
  if (!raw) return [];
  return raw
    .split(/[,;/]|\s{2,}/)
    .map((p) => p.trim())
    .filter((p) => /\d{5,}/.test(p.replace(/\D/g, '')))
    .map((p) => {
      const d = p.replace(/\D/g, '');
      const e164 =
        d.length === 12 && d.startsWith('998')
          ? `+${d}`
          : d.length === 9
            ? `+998${d}`
            : `${p.startsWith('+') ? '+' : ''}${d}`;
      return { href: `tel:${e164}`, label: formatPhone(p) };
    });
}

/** A stored website → a safe href + a short label (no scheme, no `www.`, no
 *  trailing slash). Anything that is not an absolute http(s) URL renders nothing. */
export function websiteEntry(
  raw: string | null | undefined,
): { href: string; label: string } | null {
  const href = raw?.trim();
  if (!href || !/^https?:\/\/[^\s"'<>]+$/i.test(href)) return null;
  const label = href
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/$/, '');
  return { href, label };
}

/** A stored `t.me/<x>` → an https href + a label: `@handle` for a username,
 *  the bare `t.me/…` for a phone or invite link (no username to show). */
export function telegramEntry(
  raw: string | null | undefined,
): { href: string; label: string } | null {
  const v = raw?.trim();
  const m = v?.match(/^t\.me\/((?:joinchat\/)?\+?[A-Za-z0-9_-]+)$/);
  if (!v || !m) return null;
  const isHandle = /^[A-Za-z][A-Za-z0-9_]{3,31}$/.test(m[1]);
  return { href: `https://${v}`, label: isHandle ? `@${m[1]}` : v };
}

/**
 * A winning company's phone (tap-to-call) and, when present, its email as a
 * secondary line — then, one compact line each and only when present, the
 * Yandex-Maps enrichment (2026-10-07): business category, website, Telegram and
 * the map address. Those arrive on the same paid payload as the phone, on the
 * lot outcome block and the supplier Pro header only. Renders nothing without data — and the data only exists on a
 * PAID payload: the API omits `winnerContact`/`contact` for guests, free users
 * and every prerendered page, so this component never decides entitlement
 * itself and must never be fed anything the server did not send.
 *
 * Not used on list rows: the API never sends a winner contact on a list
 * payload (anti-harvest). `tone="onBrand"` is for the inverted
 * (blue) supplier header, where the default brand-coloured link would vanish.
 */
export function WinnerContactLines({
  contact,
  tone = 'default',
  labelKey = 'tender.outcome.winnerContact',
  className = '',
  testId = 'winner-contact',
  surface = 'lot',
}: {
  contact: WinnerContactData | null | undefined;
  tone?: 'default' | 'onBrand';
  /** i18n key naming the group for screen readers. */
  labelKey?: string;
  className?: string;
  testId?: string;
  /** Where the lines sit — rides `winner_contact_clicked` as `surface`. The
   *  buyer page renders none (owner decision 2026-10-07). */
  surface?: 'lot' | 'supplier';
}) {
  const t = useT();
  /* Using the advertised Pro feature — `winner_contact_clicked{kind, surface}`
     (the delivery itself is the server's `winner_contact_viewed`). Only the
     KIND of link: never the number, address or handle. */
  const clicked = (kind: WinnerContactClickKind) => () =>
    track('winner_contact_clicked', { kind, surface });
  if (!contact) return null;
  const phones = phoneEntries(contact.phone);
  const email = contact.email?.trim() || null;
  const category = contact.category?.trim() || null;
  const site = websiteEntry(contact.website);
  const tg = telegramEntry(contact.telegram);
  const mapAddress = contact.mapAddress?.trim() || null;
  if (phones.length === 0 && !email && !category && !site && !tg && !mapAddress) return null;
  const onBrand = tone === 'onBrand';
  const lineCls = `flex items-baseline gap-1.5 text-[12.5px] [overflow-wrap:anywhere] ${
    onBrand ? 'text-white/75' : 'text-ink-soft'
  }`;
  const iconCls = 'h-3 w-3 shrink-0 self-center opacity-60';
  const linkCls = onBrand
    ? 'text-white underline decoration-white/40 underline-offset-2 transition-colors hover:decoration-white'
    : 'text-brand underline decoration-brand/30 underline-offset-2 transition-colors hover:decoration-brand';
  return (
    <div
      role="group"
      className={`flex flex-col gap-0.5 ${className}`}
      data-testid={testId}
      aria-label={t(labelKey)}
    >
      {phones.length > 0 && (
        <div
          className={`flex flex-wrap items-center gap-x-2 text-[13.5px] font-semibold tabular-nums ${
            onBrand ? 'text-white' : 'text-ink'
          }`}
        >
          <span className="sr-only">{t('tender.contactPhone')}: </span>
          <PhoneIcon aria-hidden="true" className="h-3.5 w-3.5 shrink-0 opacity-60" />
          {phones.map((p, i) => (
            <a
              key={i}
              href={p.href}
              onClick={clicked('tel')}
              className={`whitespace-nowrap ${linkCls}`}
            >
              {p.label}
            </a>
          ))}
        </div>
      )}
      {email && (
        <div
          className={`text-[12.5px] [overflow-wrap:anywhere] ${
            onBrand ? 'text-white/75' : 'text-ink-soft'
          }`}
        >
          <span className="sr-only">{t('tender.contactEmail')}: </span>
          <a href={`mailto:${email}`} onClick={clicked('email')} className={linkCls}>
            {email}
          </a>
        </div>
      )}
      {category && (
        <div className={lineCls} data-testid={`${testId}-category`}>
          <span className="sr-only">{t('tender.contactCategory')}: </span>
          <TagIcon aria-hidden="true" className={iconCls} />
          <span>{category}</span>
        </div>
      )}
      {site && (
        <div className={lineCls}>
          <span className="sr-only">{t('tender.contactWebsite')}: </span>
          <GlobeIcon aria-hidden="true" className={iconCls} />
          <a
            href={site.href}
            onClick={clicked('website')}
            target="_blank"
            rel="nofollow noopener"
            className={linkCls}
            data-testid={`${testId}-website`}
          >
            {site.label}
          </a>
        </div>
      )}
      {tg && (
        <div className={lineCls}>
          <span className="sr-only">{t('tender.contactTelegram')}: </span>
          <TelegramIcon aria-hidden="true" className={iconCls} />
          <a
            href={tg.href}
            onClick={clicked('telegram')}
            target="_blank"
            rel="nofollow noopener"
            className={linkCls}
            data-testid={`${testId}-telegram`}
          >
            {tg.label}
          </a>
        </div>
      )}
      {mapAddress && (
        <div className={lineCls} data-testid={`${testId}-address`}>
          <span className="sr-only">{t('tender.contactMapAddress')}: </span>
          <PinIcon aria-hidden="true" className={iconCls} />
          <span>{mapAddress}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Today's winner-contact ceiling is spent (200 distinct companies per account
 * and Tashkent day, server-side — `winner-contact-cap.ts`). Worded softly on
 * purpose (owner, 2026-10-07): the reader who hits it is far more likely a
 * heavy honest user than the scraper it exists for, so it names the number,
 * says why, and offers the one human who can lift it — the support Telegram
 * (`COMPANY.telegramUrl`, @flesteyto; omitted on a telegram-off deployment).
 * The limit itself is unchanged. One component for the lot outcome block and
 * the supplier header, so the two can never word it differently.
 */
export function WinnerContactCappedNote({
  tone = 'default',
  className = '',
  testId = 'winner-contact-capped',
}: {
  tone?: 'default' | 'onBrand';
  className?: string;
  testId?: string;
}) {
  const t = useT();
  const onBrand = tone === 'onBrand';
  return (
    <div className={`text-[12.5px] leading-snug ${className}`} data-testid={testId}>
      <p className={onBrand ? 'text-white/70' : 'text-ink-muted'}>
        {t('tender.outcome.winnerContactCapped')}
      </p>
      {HAS_TELEGRAM && (
        <a
          href={COMPANY.telegramUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testId}-telegram`}
          className={`mt-1.5 inline-flex h-7 items-center gap-1.5 rounded-md border px-2.5 text-[12px] font-semibold transition-colors ${
            onBrand
              ? 'border-white/30 text-white hover:border-white/60'
              : 'border-edge text-brand hover:border-brand/40'
          }`}
        >
          <TelegramIcon aria-hidden="true" className="h-3.5 w-3.5" />
          {t('tender.outcome.winnerContactCappedTelegram')}
        </a>
      )}
    </div>
  );
}

/**
 * «Это ваша компания? Скрыть телефон или исправить данные» — the quiet opt-out /
 * correction link (owner, 2026-10-07) under a winner's contact on the lot, in
 * the supplier Pro header and in the public supplier page's footer. Opens the
 * existing support form on the `company_data` topic with the ИНН filled in
 * (`ContactSupport` → POST /support → `support_submitted{topic:'company_data'}`);
 * no new backend beyond that topic. A company that does not want its phone
 * shown has one obvious, low-friction way to say so.
 */
export function CompanyDataLink({
  tin,
  tone = 'default',
  className = '',
}: {
  tin: string;
  tone?: 'default' | 'onBrand';
  className?: string;
}) {
  const t = useT();
  return (
    <div className={className} data-testid="company-data-link">
      <ContactSupport
        topic={{ kind: 'company_data', tin }}
        className={`text-left text-[12px] underline underline-offset-2 transition-colors ${
          tone === 'onBrand'
            ? 'text-white/60 decoration-white/30 hover:text-white'
            : 'text-ink-muted decoration-ink-muted/40 hover:text-ink'
        }`}
      >
        {t('companyData.link')}
      </ContactSupport>
    </div>
  );
}
